import { PendingIcon } from './icons/PendingIcon';
import { ButtonHTMLAttributes, DetailedHTMLProps, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

type Props = Omit<
  DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>,
  'children'
> & {
  pending?: boolean;
  label: ReactNode;
  labelHover?: ReactNode;
};

export function Button({ type, className, pending, disabled, label, labelHover, ...props }: Props) {
  return (
    <button
      // eslint-disable-next-line react/button-has-type
      type={type ?? 'button'}
      aria-busy={pending}
      aria-disabled={disabled}
      // TODO: disable click handlers during pending/disabled?
      className={twMerge(
        'bg-primary group font-mono text-sm uppercase text-white',
        'hover:bg-primary-hover focus:bg-primary-hover transition',
        'aria-[busy=true]:cursor-default aria-[busy=true]:bg-red-800',
        'aria-[disabled=true]:cursor-not-allowed aria-[disabled=true]:bg-neutral-800 aria-[disabled=true]:text-white/40',
        className,
      )}
      {...props}
    >
      <span className="pointer-events-none inline-grid place-items-center overflow-hidden p-[1em]">
        <span
          className={twMerge(
            'col-start-1 row-start-1 leading-none',
            'translate-y-0 opacity-100 transition',
            labelHover ? 'group-hover:-translate-y-3 group-hover:opacity-0' : null,
            'group-aria-[busy=true]:-translate-y-3 group-aria-[busy=true]:opacity-0',
          )}
        >
          {label}
        </span>

        {labelHover ? (
          <span
            aria-hidden
            className={twMerge(
              'col-start-1 row-start-1 leading-none',
              'translate-y-3 opacity-0 transition',
              'group-hover:translate-y-0 group-hover:opacity-100',
              'group-aria-[busy=true]:-translate-y-3 group-aria-[busy=true]:opacity-0',
            )}
          >
            {labelHover}
          </span>
        ) : null}

        <span
          aria-hidden
          className={twMerge(
            'col-start-1 row-start-1',
            'translate-y-3 opacity-0 transition',
            'group-aria-[busy=true]:translate-y-0 group-aria-[busy=true]:opacity-100',
          )}
        >
          {/* TODO: replace with themed pending icon */}
          <PendingIcon />
        </span>
      </span>
    </button>
  );
}
